import { Calendar, User, Trash2, ArrowUpRight, Archive, ArchiveRestore, Layers, Globe, FileText, MoreVertical, Pencil } from "lucide-react";
import { useRef, useState, useMemo, type ReactNode } from "react";
import { useConfirm } from "@omit/react-confirm-dialog";
import { Button } from "@/modern/components/ui/button-shadcn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/modern/components/ui/dropdown-menu";
import { cn } from "@/modern/utils";
import { MarketingWorkFile } from "@/bryntum-lib/models/MarketingWorkFile";
import { MarketingWorkFileCard } from "@/bryntum-lib/models/MarketingWorkFileCard";
import { MarketingAsset } from "@/bryntum-lib/models/MarketingAsset";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useFileUrl } from "@/react/components/entityViews/files/api/files.api";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { NamePromptModal } from "@/react/components/NamePromptModal";
import { useAsyncTaskUIContext } from "@/modern/components/async-tasks/async-task-ui";
import { matchBoardMarketingPageGenerationTask } from "@/modern/components/async-tasks/utils";
import { MarketingPageGeneratingWorkFilePreview } from "@/react/components/entityViews/marketingPages/components/MarketingPageGeneratingSkeleton";

const formatDate = (d: Date | string | null | undefined) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const MAX_THUMBS = 5;
const NEW_THRESHOLD_MS = 20_000;
/** Ignore card clicks briefly after dialog/menu interactions (Radix click-through). */
const CARD_CLICK_GUARD_MS = 300;

function OutputCountChip({
  icon,
  count,
  singular,
  isSelected,
  onClick,
  ariaLabel,
}: {
  icon: ReactNode;
  count: number;
  singular: string;
  isSelected: boolean;
  onClick: () => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        "inline-flex items-center gap-1 rounded px-1 py-0.5 transition-colors",
        isSelected
          ? "text-primary"
          : "text-primary/60 hover:text-primary hover:bg-primary/10",
      )}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {icon}
      {count} {singular}{count === 1 ? "" : "s"}
    </button>
  );
}

interface WorkFileCardProps {
  file: MarketingWorkFile;
  cards: MarketingWorkFileCard[];
  pageCount?: number;
  documentCount?: number;
  isSelected?: boolean;
  onSelect?: (id: number) => void;
  onArchive?: (file: MarketingWorkFile) => void;
  onUnarchive?: (file: MarketingWorkFile) => void;
  onDelete?: (file: MarketingWorkFile) => void;
  onOpen?: (id: number) => void;
}

export const WorkFileCard = ({
  file,
  cards,
  pageCount = 0,
  documentCount = 0,
  isSelected = false,
  onSelect,
  onArchive,
  onUnarchive,
  onDelete,
  onOpen,
}: WorkFileCardProps) => {
  const { project, selectedBoard } = useSwivuBryntumContext();
  const { taskMatching } = useAsyncTaskUIContext();
  const boardId = (selectedBoard?.record as { id?: number } | undefined)?.id;
  const isGeneratingPage =
    boardId != null &&
    taskMatching((task) =>
      matchBoardMarketingPageGenerationTask(task, boardId, Number(file.id)),
    );
  const confirm = useConfirm();
  const [menuOpen, setMenuOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const skipCardClickRef = useRef(false);

  const guardCardClick = () => {
    skipCardClickRef.current = true;
    window.setTimeout(() => {
      skipCardClickRef.current = false;
    }, CARD_CLICK_GUARD_MS);
  };

  const openRenameModal = () => {
    guardCardClick();
    setMenuOpen(false);
    setRenameOpen(true);
  };

  const handleRenameOpenChange = (open: boolean) => {
    if (!open) guardCardClick();
    setRenameOpen(open);
  };

  const isNew = file.createdAt != null && Date.now() - new Date(file.createdAt).getTime() < NEW_THRESHOLD_MS;
  const hubLabel = file.problemHubRecord?.label ?? null;

  const fileCards = cards
    .filter((c) => c.workFile === file.id)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const cardCount = fileCards.length;
  const firstBodyText = fileCards[0]?.bodyText ?? null;

  const creatorName = (() => {
    if (!file.creator) return null;
    const boardUser = (project as any)?.boardUsersStore?.getById(file.creator) as
      | { id: number; name?: string }
      | undefined;
    return boardUser?.name ?? null;
  })();

  const thumbAssets = useMemo(() => {
    const assetStore = project?.getCrudStore(STORE_KEYS.marketingAssetStore);
    if (!assetStore) return [] as { fileId: number; alt: string }[];
    const allAssetIds = fileCards.flatMap((c) => c.assets ?? []);
    const items: { fileId: number; alt: string }[] = [];
    for (const id of allAssetIds) {
      if (items.length >= MAX_THUMBS) break;
      const asset = assetStore.getById(id) as unknown as MarketingAsset | undefined;
      if (asset?.file) items.push({ fileId: asset.file, alt: asset.caption || "" });
    }
    return items;
  }, [project, fileCards]);

  const totalAssetCount = useMemo(
    () => fileCards.flatMap((c) => c.assets ?? []).length,
    [fileCards]
  );

  const thumbsToShow = thumbAssets;
  const overflow = Math.max(0, totalAssetCount - MAX_THUMBS);

  const handleArchive = async () => {
    const confirmed = await confirm({
      title: "Archive work file",
      description: `Archive "${file.displayName}"? It will be marked as archived and can be permanently deleted later.`,
    });
    if (confirmed) onArchive?.(file);
  };

  const handleUnarchive = () => {
    onUnarchive?.(file);
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "Delete work file",
      description: `Permanently delete "${file.displayName}"? This action cannot be undone.`,
    });
    if (confirmed) onDelete?.(file);
  };

  return (
    <>
      <article
        onClick={() => {
          if (skipCardClickRef.current) return;
          onOpen?.(file.id);
        }}
        className={cn(
          "group relative flex cursor-pointer flex-col overflow-hidden rounded-xl border bg-card text-left shadow-sm transition-all",
          isSelected
            ? "border-primary ring-2 ring-primary/30 shadow-md"
            : "border-border hover:border-foreground/30 hover:shadow-md",
          "focus-within:ring-2 focus-within:ring-ring/50",
          isNew && "animate-card-highlight",
        )}
      >
        <div className="flex flex-1 flex-col gap-2 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              {hubLabel && (
                <span className="inline-flex shrink-0 items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground border border-border/60">
                  {hubLabel}
                </span>
              )}
              {file.archived && (
                <span className="inline-flex shrink-0 items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-500">
                  Archived
                </span>
              )}
            </div>

            <DropdownMenu open={menuOpen && !renameOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Work file actions"
                >
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem className="gap-2 text-xs" onSelect={openRenameModal}>
                  <Pencil className="h-3.5 w-3.5" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {file.archived ? (
                  <DropdownMenuItem className="gap-2 text-xs" onSelect={handleUnarchive}>
                    <ArchiveRestore className="h-3.5 w-3.5" />
                    Unarchive
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem className="gap-2 text-xs" onSelect={() => void handleArchive()}>
                    <Archive className="h-3.5 w-3.5" />
                    Archive
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="gap-2 text-xs text-destructive focus:text-destructive"
                  disabled={!file.archived}
                  onSelect={() => void handleDelete()}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <h3
            className={cn(
              "line-clamp-1 text-sm font-medium leading-snug",
              file.archived ? "text-muted-foreground line-through" : "text-foreground",
            )}
          >
            {file.displayName}
          </h3>

          {firstBodyText && (
            <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground m-0">
              {firstBodyText}
            </p>
          )}

          {isGeneratingPage ? (
            <MarketingPageGeneratingWorkFilePreview />
          ) : thumbsToShow.length > 0 ? (
            <div className="mt-auto grid grid-cols-5 gap-1">
              {thumbsToShow.map(({ fileId, alt }, i) => (
                <AssetThumbItem
                  key={fileId}
                  fileId={fileId}
                  alt={alt}
                  isLast={i === thumbsToShow.length - 1 && overflow > 0}
                  overflow={overflow}
                />
              ))}
            </div>
          ) : (
            <div className="mt-auto flex h-20 items-center justify-center rounded-md border border-dashed border-border/70 bg-muted text-xs text-muted-foreground">
              No assets yet
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <User className="h-3 w-3" />
              <span className="truncate">{creatorName ?? "Unknown"}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDate(file.updatedAt)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Layers className="h-3 w-3" />
              {cardCount} card{cardCount === 1 ? "" : "s"}
            </span>
            {pageCount > 0 && (
              <OutputCountChip
                icon={<Globe className="h-3 w-3" />}
                count={pageCount}
                singular="page"
                isSelected={isSelected}
                onClick={() => onSelect?.(file.id)}
                ariaLabel="View outputs"
              />
            )}
            {documentCount > 0 && (
              <OutputCountChip
                icon={<FileText className="h-3 w-3" />}
                count={documentCount}
                singular="document"
                isSelected={isSelected}
                onClick={() => onSelect?.(file.id)}
                ariaLabel="View documents"
              />
            )}
          </div>

          <ArrowUpRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" />
        </div>
      </article>

      <NamePromptModal
        open={renameOpen}
        onOpenChange={handleRenameOpenChange}
        title="Rename work file"
        description="Enter a new name for this marketing work file."
        fieldLabel="Work file name"
        placeholder="Enter work file name"
        initialValue={file.displayName}
        onConfirm={(name) => {
          file.name = name;
        }}
      />
    </>
  );
};

const AssetThumbItem = ({
  fileId,
  alt,
  isLast,
  overflow,
}: {
  fileId: number;
  alt: string;
  isLast: boolean;
  overflow: number;
}) => {
  const { displayUrl: src } = useFileUrl(fileId);
  return (
    <div className="relative aspect-square overflow-hidden rounded-md border border-border bg-muted">
      {src && (
        <img src={src} alt={alt} loading="lazy" className="h-full w-full object-cover" />
      )}
      {isLast && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/70 text-xs font-medium text-foreground backdrop-blur-sm">
          +{overflow}
        </div>
      )}
    </div>
  );
};
